// sessionIdentity.ts - which signed-in identity this tab's user-specific state belongs to.
//
// User data lives in places that outlive a login: the redux store, shared in-flight
// requests, component state, session storage. When the identity changes inside one JS
// instance (logout, or a different account arriving via token swap - no page reload),
// the "session epoch" advances. Holders reset on it, and apiClient drops any response
// whose request was issued under an older epoch, so a late answer for the previous
// identity can never repopulate state under the new one.
//
// Leaf module: no imports from the rest of the app.

type Listener = () => void;

export const SESSION_SUPERSEDED_MESSAGE = 'Request superseded by a session change';

export class SessionSupersededError extends Error {
  readonly code = 'SESSION_SUPERSEDED';

  constructor() {
    super(SESSION_SUPERSEDED_MESSAGE);
    this.name = 'SessionSupersededError';
  }
}

export const isSessionSupersededError = (error: unknown): boolean =>
  (error as { code?: unknown } | null | undefined)?.code === 'SESSION_SUPERSEDED';

export type SessionEndReason = 'DMS_SIGNED_OUT' | 'DMS_ACCOUNT_CHANGED';
type EndingListener = (reason: SessionEndReason) => void;

let epoch = 0;
// null: nothing observed yet; '': signed out; otherwise the account key (token sub / user id).
let identityKey: string | null = null;
const listeners = new Set<Listener>();
const endingListeners = new Set<EndingListener>();

// Runs BEFORE the epoch advances (and so before user-scoped state is reset), so the
// outgoing account can still clean up server-side resources it owns. Never throws.
const notifySessionEnding = (reason: SessionEndReason) => {
  endingListeners.forEach((listener) => {
    try {
      listener(reason);
    } catch {
      // best effort: a cleanup failure must not block the session change
    }
  });
};

const advanceEpoch = () => {
  epoch += 1;
  listeners.forEach((listener) => listener());
};

export const getSessionEpoch = (): number => epoch;

export const subscribeSessionEnding = (listener: EndingListener): (() => void) => {
  endingListeners.add(listener);
  return () => {
    endingListeners.delete(listener);
  };
};

export const subscribeSessionEpoch = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Record the current account ('' when signed out). The epoch advances when one
 * account is replaced by another or by signed-out; the first observation and a
 * sign-in from signed-out only set the baseline (nothing to discard).
 */
export const syncSessionIdentity = (key: string): boolean => {
  const previous = identityKey;
  identityKey = key;
  if (!previous || previous === key) {
    return false;
  }
  notifySessionEnding(key === '' ? 'DMS_SIGNED_OUT' : 'DMS_ACCOUNT_CHANGED');
  advanceEpoch();
  return true;
};

/** The outgoing identity's state ends here, even if the same account signs in again. */
export const endSession = (): void => {
  if (identityKey === '') {
    return;
  }
  identityKey = '';
  notifySessionEnding('DMS_SIGNED_OUT');
  advanceEpoch();
};

// Every logout path (Keycloak, backend auth, 401 handling) broadcasts this event.
if (typeof window !== 'undefined') {
  window.addEventListener('dms-auth-logout', endSession);
}
